import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Image as ImageIcon, Eye, Trash2, Paperclip } from 'lucide-react';
import { ActivityAttachment } from '../types';
import { activityAttachmentsService } from '../services/activityAttachmentsService';

interface ActivityAttachmentsViewerProps {
  activityId: string;
  onClose: () => void;
  onDelete?: () => void;
}

const ActivityAttachmentsViewer: React.FC<ActivityAttachmentsViewerProps> = ({
  activityId,
  onClose,
  onDelete
}) => {
  const [attachments, setAttachments] = useState<ActivityAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttachment, setSelectedAttachment] = useState<ActivityAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    loadAttachments();
  }, [activityId]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      const data = await activityAttachmentsService.getAttachmentsByActivity(activityId);
      setAttachments(data);
    } catch (error) {
      console.error('Error loading attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachment: ActivityAttachment) => {
    try {
      const url = await activityAttachmentsService.getDownloadUrl(attachment.filePath);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error al descargar el archivo');
    }
  };

  const handlePreview = async (attachment: ActivityAttachment) => {
    if (!activityAttachmentsService.isImageFile(attachment.fileType) &&
        !activityAttachmentsService.isPdfFile(attachment.fileType)) {
      handleDownload(attachment);
      return;
    }

    try {
      setLoadingPreview(true);
      setSelectedAttachment(attachment);
      const url = await activityAttachmentsService.getDownloadUrl(attachment.filePath);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error loading preview:', error);
      alert('Error al cargar la vista previa');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      return;
    }

    try {
      await activityAttachmentsService.deleteAttachment(attachmentId);
      await loadAttachments();
      onDelete?.();
      alert('Archivo eliminado correctamente');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Error al eliminar el archivo');
    }
  };

  const closePreview = () => {
    setSelectedAttachment(null);
    setPreviewUrl('');
  };

  const getFileIcon = (fileType: string) => {
    if (activityAttachmentsService.isImageFile(fileType)) {
      return ImageIcon;
    }
    return FileText;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Cargando archivos adjuntos...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Attachments List */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Paperclip className="w-5 h-5 mr-2" />
              Archivos Adjuntos ({attachments.length})
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {attachments.length === 0 ? (
              <div className="text-center py-12">
                <Paperclip className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay archivos adjuntos en esta actividad</p>
              </div>
            ) : (
              <div className="space-y-3">
                {attachments.map((attachment) => {
                  const Icon = getFileIcon(attachment.fileType);
                  return (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {activityAttachmentsService.isImageFile(attachment.fileType) ? (
                            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                              <ImageIcon className="w-6 h-6 text-blue-600" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <Icon className="w-6 h-6 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {attachment.fileName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {activityAttachmentsService.formatFileSize(attachment.fileSize)} •
                            {' '}{new Date(attachment.uploadedAt).toLocaleDateString()} •
                            {' '}{attachment.uploadedBy}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handlePreview(attachment)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver/Descargar"
                        >
                          {activityAttachmentsService.isImageFile(attachment.fileType) ||
                           activityAttachmentsService.isPdfFile(attachment.fileType) ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDownload(attachment)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Descargar"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(attachment.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {selectedAttachment && previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4">
          <button
            onClick={closePreview}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="max-w-6xl max-h-[90vh] overflow-auto">
            {loadingPreview ? (
              <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            ) : activityAttachmentsService.isImageFile(selectedAttachment.fileType) ? (
              <img
                src={previewUrl}
                alt={selectedAttachment.fileName}
                className="max-w-full max-h-full rounded-lg"
              />
            ) : activityAttachmentsService.isPdfFile(selectedAttachment.fileType) ? (
              <iframe
                src={previewUrl}
                className="w-full h-[80vh] rounded-lg bg-white"
                title={selectedAttachment.fileName}
              />
            ) : null}
          </div>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-lg shadow-lg">
            <p className="text-sm font-medium text-gray-900">{selectedAttachment.fileName}</p>
            <p className="text-xs text-gray-500">
              {activityAttachmentsService.formatFileSize(selectedAttachment.fileSize)}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ActivityAttachmentsViewer;
